import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useDataSelectionContext } from "../DataSelectionContext";
import DataSelectionModel from "../DataSelectionModal";
import { Form, FormGroup } from "react-bootstrap";
import { useLayerService } from "../../../hooks";
import { Category, Group } from "../../../types/OsPoi/OsPoiTypes";
import OsPoiClassificationsField from "./OsPoiClassificationsFields";
import LoadingSpinner from "../../Loading/LoadingSpinner";
import { WfsGetOSPoiRequest } from "../../../types/Wfs/WfsGetOsPoiRequest";

interface OsPoiFormValues {
    terminals: number;
    classifications: number[];
    outputFormat: string;
}

const OsPoi = () => {
    const layerName = "os:poi";
    const { layers, getLayer, wfsOrder } = useDataSelectionContext();
    const layer = useMemo(() => getLayer(layerName), [layers]);
    const layerService = useLayerService();
    const [groups, setGroups] = useState<Group[]>();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        layerService.GetOsPoiGroups()
            .then((values) => {

                // Process the values...
                values.forEach(group => {
                    group.parent = undefined;
                    group.children = group.categories;

                    group.children.forEach(category => {
                        category.parent = group
                        category.children = (category as Category).classifications

                        category.children.forEach(classification => {
                            classification.parent = category;
                            classification.children = undefined;
                        })
                    })
                });

                setGroups(values);
                setLoading(false);
            })
            .catch((err) => {

            });
    }, []);

    const initialValues = useMemo(() => ({
        classifications: [],
        outputFormat: "csv",
        terminals: 5
    } as OsPoiFormValues),
        [layer]);

    const schema = useMemo(() => 
        yup.object({
            classifications: yup.array()
                .min(1, "You must select at least one category or classification"),
            outputFormat: yup
                .string()
                .oneOf(["csv", "gml"])
                .required(),
            terminals: yup.number().min(5)
        })
    , [layer])

    const onSubmit = (values: OsPoiFormValues) => {
        const filter = "ClassificationId IN " + values.classifications.join(",");

        wfsOrder({
            service: "WFS",
            version: "2.0.0",
            request: "GetFeature",
            resultType: "Results",
            typeNames: layerName,
            outputFormat: values.outputFormat,
            filter: filter,
            users: values.terminals
        } as WfsGetOSPoiRequest);
    }

    return (
        <DataSelectionModel
            headerText="OS Points of Interest"
            layer={layer}
            size="xl"
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={onSubmit}>
            {({ values, errors, handleChange, handleBlur }) => (
                <>
                    {loading && <LoadingSpinner />}
                    {!loading && <> 
                        <FormGroup>
                            {groups && <OsPoiClassificationsField poiChildren={groups} />}
                            {errors.classifications && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {errors.classifications}
                                </Form.Control.Feedback>
                            )}
                        </FormGroup>
                        <Form.Group style={{ width: 300 }}>
                            <Form.Label htmlFor="outputFormat">Format</Form.Label>
                            <Form.Select
                                id="outputFormat"
                                name="outputFormat"
                                value={values.outputFormat}
                                onChange={handleChange}
                                onBlur={handleBlur}>
                                <option value="gml">GML 3</option>
                                <option value="csv">CSV</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group style={{ width: 300 }}>
                            <Form.Label htmlFor="terminals">Terminals</Form.Label>
                            <Form.Select
                                id="terminals"
                                name="terminals"
                                value={values.terminals}
                                onChange={handleChange}
                                onBlur={handleBlur}>
                                <option value={5}>5 or less</option>
                                {[...Array(20)].map((_, i) => (
                                    <option key={i + 6} value={i + 6}>
                                        {i + 6}
                                    </option>
                                ))}
                                <option value={26}>26 or more</option>
                            </Form.Select>
                        </Form.Group>
                    </>}

                </>
            )}
        </DataSelectionModel>
    );
}

export default OsPoi;